import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicQuote, downloadPublicQuotePDF } from '../services/api';
import { ClipboardList, Download, Loader2, AlertTriangle, CheckCircle, Clock, XCircle } from 'lucide-react';
import { toast, Toaster } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-100 text-slate-700', icon: Clock },
  sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700', icon: Clock },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Vencida', color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
  converted: { label: 'Convertida a venta', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch { return dateStr.slice(0, 10); }
};

const PublicQuoteView = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  useEffect(() => {
    loadQuote();
  }, [token]);

  const loadQuote = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPublicQuote(token);
      setQuote(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Cotizacion no encontrada');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloadingPDF(true);
    try {
      const res = await downloadPublicQuotePDF(token);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cotizacion_${quote.folio}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch {
      toast.error('Error al descargar PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-600 animate-spin mx-auto" />
          <p className="mt-4 text-slate-600">Cargando cotizacion...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Cotizacion No Disponible</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[quote.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusCfg.icon;
  const isExpiredOrRejected = quote.status === 'expired' || quote.status === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <Toaster position="top-center" richColors />

      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-2xl mb-4">
            <ClipboardList className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{quote.business_name}</h1>
          <p className="text-slate-600 mt-1">Cotizacion {quote.folio}</p>
          {quote.business_address && <p className="text-sm text-slate-500 mt-1">{quote.business_address}</p>}
        </div>

        {/* Warning banner for expired/rejected */}
        {isExpiredOrRejected && (
          <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            quote.status === 'expired' ? 'bg-yellow-50 border border-yellow-200' : 'bg-red-50 border border-red-200'
          }`}>
            <AlertTriangle className={`w-5 h-5 ${quote.status === 'expired' ? 'text-yellow-600' : 'text-red-600'}`} />
            <span className={`text-sm font-medium ${quote.status === 'expired' ? 'text-yellow-800' : 'text-red-800'}`}>
              {quote.status === 'expired' ? 'Esta cotizacion ha vencido.' : 'Esta cotizacion fue rechazada.'}
            </span>
          </div>
        )}

        {/* Status + Info */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusCfg.color}`}>
              <StatusIcon className="w-4 h-4" />
              {statusCfg.label}
            </span>
            <div className="text-right text-sm text-slate-500">
              <p>Fecha: {formatDate(quote.created_at)}</p>
              <p>Vigencia: {formatDate(quote.expiry_date)}</p>
            </div>
          </div>

          {/* Client info */}
          {quote.client && (
            <div className="border-t border-slate-100 pt-4 mb-4">
              <p className="text-sm text-slate-500 mb-1">Cliente</p>
              <p className="font-medium text-slate-900">{quote.client.name}</p>
              {quote.client.company && quote.client.company !== quote.client.name && (
                <p className="text-sm text-slate-600">{quote.client.company}</p>
              )}
              {quote.client.email && <p className="text-sm text-slate-500">{quote.client.email}</p>}
              {quote.client.phone && <p className="text-sm text-slate-500">{quote.client.phone}</p>}
            </div>
          )}

          {/* Items table */}
          <div className="border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700 mb-3">Conceptos</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Concepto</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Cant.</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">P. Unitario</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(quote.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <span className="font-medium text-slate-900">{item.concept}</span>
                        {item.description && <span className="block text-xs text-slate-500">{item.description}</span>}
                      </td>
                      <td className="px-3 py-2 text-right">{item.quantity}</td>
                      <td className="px-3 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-slate-100 mt-4 pt-4">
            <div className="flex flex-col items-end space-y-1">
              <div className="flex justify-between w-64 text-sm">
                <span className="text-slate-500">Subtotal</span>
                <span>{formatCurrency(quote.subtotal)}</span>
              </div>
              {quote.total_discount > 0 && (
                <div className="flex justify-between w-64 text-sm">
                  <span className="text-slate-500">Descuento</span>
                  <span className="text-red-600">-{formatCurrency(quote.total_discount)}</span>
                </div>
              )}
              {quote.total_tax > 0 && (
                <div className="flex justify-between w-64 text-sm">
                  <span className="text-slate-500">Impuestos</span>
                  <span>{formatCurrency(quote.total_tax)}</span>
                </div>
              )}
              <div className="flex justify-between w-64 text-lg font-bold border-t border-slate-200 pt-2">
                <span>TOTAL</span>
                <span className="text-teal-600">{formatCurrency(quote.total)}</span>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          {(quote.notes || quote.terms) && (
            <div className="border-t border-slate-100 mt-4 pt-4 space-y-3">
              {quote.notes && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Notas</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{quote.notes}</p>
                </div>
              )}
              {quote.terms && (
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">Condiciones</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{quote.terms}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Download button */}
        <div className="text-center">
          <button
            onClick={handleDownloadPDF}
            disabled={downloadingPDF}
            className="inline-flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {downloadingPDF ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            {downloadingPDF ? 'Generando...' : 'Descargar PDF'}
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          Powered by Todo en Local
        </p>
      </div>
    </div>
  );
};

export default PublicQuoteView;
